import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MobileStepper,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { useCallback, useEffect, useRef, useState } from 'react';
import useAnnouncements from '@modules/announcements/hooks/useAnnouncements';
import DOMPurify from 'dompurify';

interface PendingAnnouncement {
  id: number;
  title: string;
  contentHtml: string;
  publishedAt: string;
}

const AnnouncementPopup = () => {
  const { getPending, recordInteraction } = useAnnouncements();
  const [announcements, setAnnouncements] = useState<PendingAnnouncement[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [open, setOpen] = useState(false);
  const displayedRef = useRef<Set<number>>(new Set());
  const readTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const fetchPending = useCallback(async () => {
    const result = await getPending();
    if (result.success && result.data?.items && result.data.items.length > 0) {
      setAnnouncements(result.data.items as PendingAnnouncement[]);
      setActiveIndex(0);
      setOpen(true);
    }
  }, [getPending]);

  useEffect(() => {
    // Fetch pending announcements on mount (with slight delay to not block initial render)
    const timer = setTimeout(fetchPending, 2000);
    return () => clearTimeout(timer);
  }, [fetchPending]);

  // Track "displayed" when popup opens for an announcement
  useEffect(() => {
    if (!open || announcements.length === 0) {
      return;
    }

    const current = announcements[activeIndex];
    if (current && !displayedRef.current.has(current.id)) {
      displayedRef.current.add(current.id);
      recordInteraction(current.id, 'DISPLAYED');
    }

    // Start "read" timer (5 seconds)
    readTimerRef.current = setTimeout(() => {
      if (current) {
        recordInteraction(current.id, 'READ');
      }
    }, 5000);

    return () => {
      if (readTimerRef.current) {
        clearTimeout(readTimerRef.current);
      }
    };
  }, [open, activeIndex, announcements, recordInteraction]);

  // Track link clicks via event delegation
  useEffect(() => {
    const container = contentRef.current;
    if (!container || !open || announcements.length === 0) {
      return;
    }

    const handleClick = (e: MouseEvent) => {
      const anchor = (e.target as HTMLElement).closest('a');
      if (!anchor) {
        return;
      }

      const href = anchor.getAttribute('href');
      if (!href || href.startsWith('#') || href.startsWith('mailto:')) {
        return;
      }

      const current = announcements[activeIndex];
      if (current) {
        recordInteraction(current.id, 'CLICKED', { url: href });
      }
    };

    container.addEventListener('click', handleClick);
    return () => container.removeEventListener('click', handleClick);
  }, [open, activeIndex, announcements, recordInteraction]);

  const handleClose = () => {
    // Dismiss all remaining
    for (let i = activeIndex; i < announcements.length; i++) {
      recordInteraction(announcements[i].id, 'DISMISSED');
    }
    setOpen(false);
  };

  if (announcements.length === 0) {
    return null;
  }

  const current = announcements[activeIndex];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 1.5 },
      }}
    >
      <DialogTitle>
        <Typography variant="h6" fontWeight={600}>
          {current?.title}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {current && (
          <Box
            ref={contentRef}
            sx={{
              '& img': { maxWidth: '100%', height: 'auto', borderRadius: 1 },
              '& h2': { fontSize: '1.3rem', fontWeight: 600 },
              '& h3': { fontSize: '1.1rem', fontWeight: 600 },
              '& a': { color: 'primary.main', textDecoration: 'underline', cursor: 'pointer' },
              '& ul, & ol': { pl: 3 },
              lineHeight: 1.6,
            }}
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(current.contentHtml, {
                ADD_TAGS: ['img', 'a'],
                ADD_ATTR: ['src', 'alt', 'width', 'height', 'style', 'href', 'target', 'rel'],
              }),
            }}
          />
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2 }}>
        {announcements.length > 1 && (
          <MobileStepper
            variant="dots"
            steps={announcements.length}
            position="static"
            activeStep={activeIndex}
            sx={{ flex: 1, bgcolor: 'transparent' }}
            backButton={
              <Button
                size="small"
                onClick={() => setActiveIndex((prev) => prev - 1)}
                disabled={activeIndex === 0}
                startIcon={<KeyboardArrowLeftIcon />}
              >
                Précédent
              </Button>
            }
            nextButton={
              <Button
                size="small"
                onClick={() => setActiveIndex((prev) => prev + 1)}
                disabled={activeIndex === announcements.length - 1}
                endIcon={<KeyboardArrowRightIcon />}
              >
                Suivant
              </Button>
            }
          />
        )}
        {announcements.length <= 1 && <Box sx={{ flex: 1 }} />}
        <IconButton size="small" onClick={handleClose} sx={{ ml: 'auto' }}>
          <CloseIcon />
        </IconButton>
      </DialogActions>
    </Dialog>
  );
};

export default AnnouncementPopup;
